import { api } from './api';
import { userApi } from './user';

// A share is redeemed as an identity: every route here is authenticated, so
// nothing about the share is readable without a session in hand.
const shareApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // The share landing page's view of a token; authenticated, so this only
    // answers with a session in hand.
    shareAcceptDetails: builder.query<ShareDetails, string>({
      query: (token) => `shares/accept-details/${token}`,
    }),
    acceptShare: builder.mutation<ShareAccepted, string>({
      query: (token) => ({
        url: `shares/${token}/accept`,
        method: 'POST',
        body: {},
      }),
      onQueryStarted: async (token, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          userApi.util.updateQueryData('me', undefined, (current) => {
            current.invitations = (current.invitations ?? []).filter(
              (invitation) => invitation.token !== token,
            );
          }),
        );
      },
    }),
    declineShare: builder.mutation<void, string>({
      query: (token) => ({
        url: `shares/${token}/decline`,
        method: 'POST',
        body: {},
      }),
      onQueryStarted: async (token, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          userApi.util.updateQueryData('me', undefined, (current) => {
            current.invitations = (current.invitations ?? []).filter(
              (invitation) => invitation.token !== token,
            );
          }),
        );
      },
    }),
  }),
});

// The state the share is in when it is redeemed. This is authoritative:
// the emailed copy was written when the share was issued and may have
// assumed otherwise. An unknown token answers 404, not a state.
export type ShareState = 'pending' | 'expired' | 'spent';

export type ShareDetails = {
  state: ShareState;
  label?: string;
  sharerName?: string | null;
  // The owning organisation's name and logo, resolved server-side; null
  // when the share carries no owner or the resolution failed — the page
  // then renders without a logo.
  ownerName?: string | null;
  ownerAvatarUrl?: string | null;
};

export type ShareAccepted = {
  object: { namespace: string; key: string };
};

export const {
  useShareAcceptDetailsQuery,
  useAcceptShareMutation,
  useDeclineShareMutation,
} = shareApi;

export { shareApi };
